import React, { createContext, useCallback, useContext, useState } from "react"

const AgentChatContext = createContext(null)

function makeTab(counter) {
  return { id: crypto.randomUUID(), label: `Session ${counter}`, messages: [], activeModel: null }
}

function makeAgentState() {
  const tab = makeTab(1)
  return { tabs: [tab], activeTabId: tab.id, tabCounter: 1 }
}

export function AgentChatProvider({ children }) {
  const [sessions, setSessions] = useState({
    jarvis: makeAgentState(),
    friday: makeAgentState(),
  })

  const addTab = useCallback((agent) => {
    setSessions((prev) => {
      const s = prev[agent]
      const counter = s.tabCounter + 1
      const tab = makeTab(counter)
      return { ...prev, [agent]: { ...s, tabs: [...s.tabs, tab], activeTabId: tab.id, tabCounter: counter } }
    })
  }, [])

  const closeTab = useCallback((agent, tabId) => {
    setSessions((prev) => {
      const s = prev[agent]
      if (s.tabs.length <= 1) return prev
      const newTabs = s.tabs.filter((t) => t.id !== tabId)
      const newActiveId = s.activeTabId === tabId ? newTabs[newTabs.length - 1].id : s.activeTabId
      return { ...prev, [agent]: { ...s, tabs: newTabs, activeTabId: newActiveId } }
    })
  }, [])

  const setActiveTabId = useCallback((agent, tabId) => {
    setSessions((prev) => ({ ...prev, [agent]: { ...prev[agent], activeTabId: tabId } }))
  }, [])

  const updateTab = useCallback((agent, tabId, updater) => {
    setSessions((prev) => {
      const s = prev[agent]
      return { ...prev, [agent]: { ...s, tabs: s.tabs.map((t) => t.id === tabId ? { ...t, ...updater(t) } : t) } }
    })
  }, [])

  return (
    <AgentChatContext.Provider value={{ sessions, addTab, closeTab, setActiveTabId, updateTab }}>
      {children}
    </AgentChatContext.Provider>
  )
}

export function useAgentTabs(agent) {
  const { sessions, addTab, closeTab, setActiveTabId } = useContext(AgentChatContext)
  const s = sessions[agent]
  return {
    tabs: s.tabs,
    activeTabId: s.activeTabId,
    addTab: () => addTab(agent),
    closeTab: (tabId) => closeTab(agent, tabId),
    setActiveTabId: (tabId) => setActiveTabId(agent, tabId),
  }
}

export function useAgentChat(agent, tabId) {
  const { sessions, updateTab } = useContext(AgentChatContext)
  const s = sessions[agent]
  const tab = s.tabs.find((t) => t.id === tabId) ?? s.tabs[0]

  const setMessages = useCallback(
    (updater) => updateTab(agent, tab.id, (t) => ({
      messages: typeof updater === "function" ? updater(t.messages) : updater,
    })),
    [updateTab, agent, tab.id],
  )

  const setActiveModel = useCallback(
    (model) => updateTab(agent, tab.id, () => ({ activeModel: model })),
    [updateTab, agent, tab.id],
  )

  const setTabLabel = useCallback(
    (label) => updateTab(agent, tab.id, () => ({ label })),
    [updateTab, agent, tab.id],
  )

  return { messages: tab.messages, setMessages, activeModel: tab.activeModel, setActiveModel, setTabLabel }
}
